//Imports
import React, { useCallback, useEffect, useMemo, useState } from "react";

//Hooks
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//Controls
import {
    Button,
    Form,
    Input,
    Modal,
    Select,
    TextArea,
    InputNumeric
} from "@gpstrackit/gtc-react-controls";

//Components
import {
    NotificationManager,
    NotificationContainer,
} from "Components/Notifications";

//Slice
import {
    manageWorkOrderTypeCreateWorkOrderType,
    manageWorkOrderTypeSearchDynamicForms,
    manageWorkOrderTypeEditWorkOrderType,
    manageWorkOrderTypeGetWorkOrderType,
    reset,
} from "./redux/slice";


const INIT_DATA = {
    name: undefined,
    description: undefined,
    estimatedDurationMins: undefined,
    assingForm: undefined
};

const CreateEditManageWorkOrderType = ({ refresh }) => {

    //Local States
    const [formData, setFormData] = useState(INIT_DATA);
    const { messages } = useIntl();

    //Form refs
    const createEditCrewFormRef = Form.useForm();

    //Redux States
    const { loading, dinamicForms, open, create, update, error, workOrderType, workOrderTypeId } = useSelector(state => state.manageCreateEditWorkOrderTypeRedux);

    const dispatch = useDispatch();

    //Handlers and utils
    const handleClose = () => {
        setFormData(INIT_DATA);
        dispatch(reset());
    };

    const handleSuccess = () => {
        NotificationManager.success(
            messages['manage_createEditWorkOrderType_createSuccess'],
            messages["success"],
            3000
        );
        refresh();
        handleClose();
    };

    const applyChanges = useCallback(() => {
        const { assingForm, ...otherFormData } = formData || {};
        const dynamicFormId = assingForm?.id;
        const payload = {
            ...otherFormData,
            dynamicFormId: dynamicFormId || 0,
            estimatedDurationMins: otherFormData?.estimatedDurationMins || 0,
            ...(workOrderTypeId && { id: workOrderTypeId }),
        };

        if (workOrderTypeId) {
            dispatch(manageWorkOrderTypeEditWorkOrderType(payload));
        } else {
            dispatch(manageWorkOrderTypeCreateWorkOrderType(payload));
        }
    }, [formData, workOrderTypeId, dispatch]);

    //Effects
    useEffect(() => {
        if (open) {
            dispatch(manageWorkOrderTypeSearchDynamicForms({}))
        }
    }, [open]);

    useEffect(() => {
        if (create || update) {
            handleSuccess();
        }
    }, [create, update]);

    useEffect(() => {
        if (error) {
            let currentError = error?.error
            if (currentError.includes("duplicated")) {
                currentError = messages['manage_createEditWorkOrderType_duplicatedName']
            }
            NotificationManager.error(
                currentError || messages["errorMessage"],
                messages["error"],
                3000
            );
            dispatch(reset({ error: null }));
        }
    }, [error])

    useEffect(() => {
        if (workOrderTypeId) {
            dispatch(manageWorkOrderTypeGetWorkOrderType({ id: workOrderTypeId }))
        }
    }, [workOrderTypeId])

    useEffect(() => {
        if (workOrderType && open) {
            let payload = {
                ...workOrderType,
                assingForm: workOrderType?.dynamicFormId ? workOrderType?.dynamicForm : null
            }
            setFormData(payload);
            createEditCrewFormRef.setValues(payload);
        }
    }, [workOrderType, dinamicForms, open])

    return (
        <Modal
            title={workOrderTypeId ? `${messages['manage_createEditWorkOrderType_editTitle']} ${workOrderType?.name ? `"${workOrderType?.name}"` : ""}` : messages['manage_createEditWorkOrderType_createTitle']}
            open={open}
            handleClose={handleClose}
            position="right"
            style={{ zIndex: "auto" }}
            size="small"
            loading={loading}
            footer={
                <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <Button
                        isDisabled={loading}
                        style={{ marginRight: 8 }}
                        onClick={handleClose}
                        variant="secondary"
                    >
                        {messages["cancel"]}
                    </Button>
                    <Button
                        isDisabled={loading}
                        onClick={() => {
                            createEditCrewFormRef?.submit();
                        }}
                        variant="primary"
                    >
                        {messages["save"]}
                    </Button>
                </div>
            }
        >
            <div style={{ padding: 24 }}>
                <Form
                    ref={createEditCrewFormRef.ref}
                    loading={false}
                    onValidSubmit={() => applyChanges()}
                    onChangeValues={() => { }}
                >
                    <Form.Item
                        name="name"
                        label={messages['manage_createEditWorkOrderType_name']}
                        rules={[
                            { required: true, message: messages["required_field"] },
                        ]}
                    >
                        <Input
                            id="name"
                            type="text"
                            placeholder={messages['manage_createEditWorkOrderType_name']}
                            onChange={(value) => setFormData({ ...formData, name: value })}
                            maxLength={50}
                        />
                    </Form.Item>
                    <Form.Item
                        name="assingForm"
                        label={messages['manage_createEditWorkOrderType_assingForm']}
                    >
                        <Select
                            id="assingForm"
                            options={dinamicForms || []}
                            textField="name"
                            valueField="id"
                            placeholder={messages['manage_createEditWorkOrderType_assingForm']}
                            style={{ width: "100%" }}
                            onChange={(value) => setFormData({ ...formData, assingForm: value })}
                            isSearchable
                            isClearable
                        />
                    </Form.Item>
                    <Form.Item
                        name="description"
                        label={messages['manage_createEditWorkOrderType_description']}
                    >
                        <TextArea
                            id="description"
                            placeholder={messages['manage_createEditWorkOrderType_description']}
                            maxLength={1000}
                            onChange={(value) => setFormData({ ...formData, description: value })}
                        />
                    </Form.Item>
                    <Form.Item
                        name="estimatedDurationMins"
                        label={messages['manage_createEditWorkOrderType_estimatedDurationMins']}
                        rules={[
                            { max: 43800, message: `${messages['dynamicFormsDesign_maxValue']}: 43800` },
                            { min: 1, message: `${messages['dynamicFormsDesign_minValue']}: 1` },
                        ]}
                    >
                        <InputNumeric
                            id="estimatedDurationMins"
                            placeholder={messages['manage_createEditWorkOrderType_estimatedDurationMins']}
                            isInteger
                            min={1}
                            max={43800}
                            onChange={(value) => setFormData({ ...formData, estimatedDurationMins: value || null })}
                            maxLength={5}
                            onlyPositive
                        />
                    </Form.Item>
                </Form>
            </div>
            <NotificationContainer />
        </Modal>
    );
};

export default CreateEditManageWorkOrderType;